/* eslint-disable max-params */
/* eslint-disable max-lines-per-function */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { GetTranslateSegmentsService } from '../get-translate-segments-service';
import { FactoryPsHttpSSE, PsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import { getCurrentLanguage } from '@telecom/shared/utils/language-tool';
import type { TokenUsage } from '@/infrastructure/api-functions/core-drafting-api/types';
import type { StreamStatus } from '@/domains/disclosure/enums';
import { create, Emitter } from '@patsnap/tahiti-core';
import { TranslateLoadingService } from '../translate-loading-service';
import { SseState } from '../../types';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { TranslateModel } from '../../models/translate-model';
import { parseHtml } from '@telecom/shared/utils/html';

interface ApiGetTranslateSegmentsResponse {
    content: {
        graph_index: number;
        src_text: string;
        translated_text: string;
    }[];
    token_usage: TokenUsage;
    status: StreamStatus;
}

@injectable()
export class GetTranslateSegmentsServiceImpl implements GetTranslateSegmentsService {
    constructor(
        @inject(FactoryPsHttpSSE)
        private _factorySsePsHttp: FactoryPsHttpSSE,
        @inject(TranslateModel)
        private _aiTranslationModel: TranslateModel,
        @inject(TranslateLoadingService)
        private _translateLoadingService: TranslateLoadingService,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
    ) {}

    private _stateChangeEmitter = new Emitter<SseState>();

    private _fireStateChange(state: SseState) {
        this._stateChangeEmitter.fire(state);
    }

    get onStateChange() {
        return this._stateChangeEmitter.event;
    }

    private _disposes: (() => void)[] = [];

    private _sse: PsHttpSSE<ApiGetTranslateSegmentsResponse> | null = null;

    getTranslateSegments(taskId: string) {
        const deferred = create<void>();
        this._sse = this._getTranslateSegmentsFromServer(taskId)
            .onOpen(() => {
                this._translateLoadingService.setWaitingFirstResponse(true);
                this._fireStateChange(SseState.WaitingFirstOutput);
            })
            .onMessage(data => {
                this._translateLoadingService.setWaitingFirstResponse(false);
                this._updateTranslateSegmentsBuffer(data.data);
                this._fireStateChange(SseState.Outputting);
            })
            .onError(err => {
                this._fireStateChange(SseState.OutputFailed);
                deferred.reject(err);
            })
            .onFinish(() => {
                this._fireStateChange(SseState.OutputCompleted);
                deferred.resolve();
            });
        this._sse.start();
        return deferred.promise;
    }

    abort() {
        this._disposes.forEach(dispose => dispose());
        this._disposes.length = 0;
        this._sse?.close();
    }

    retry() {
        const taskId = this._currentTaskService.taskId;
        this.getTranslateSegments(taskId);
    }

    private _updateTranslateSegments(data: ApiGetTranslateSegmentsResponse) {
        const { content } = data;
        this._aiTranslationModel.update(state => {
            state.result = content.map(item => ({
                original: parseHtml(item.src_text),
                translation: parseHtml(item.translated_text),
                graphIndex: item.graph_index,
            }));
            return state;
        });
    }

    /**
     * 缓冲一下对 _updateTranslateSegments 的调用
     * @description 使用 requestAnimationFrame 来限制更新频率
     */
    private _updateTranslateSegmentsBuffer(data: ApiGetTranslateSegmentsResponse) {
        const callback = () => {
            this._updateTranslateSegments(data);
        };
        const requestAnimationFrameId = requestAnimationFrame(callback);
        this._disposes.push(() => cancelAnimationFrame(requestAnimationFrameId));
    }

    private _getTranslateSegmentsFromServer(taskId: string) {
        const sse = this._factorySsePsHttp<ApiGetTranslateSegmentsResponse>().init({
            baseURL:coreTelecomApiUrlPrefix,
            url: '/ai-translation/translation/sync',
            data: {
                task_id: taskId,
            },
            headers: {
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
        });
        return sse;
    }
}
